import React, { Fragment, useEffect, useMemo, useState } from "react";

import { Dialog, Transition } from "@headlessui/react";
import { toast } from "react-toastify";

import apiService from "../services/api";

export const canManageResellerSettings = (user) =>
  user?.role === "user" && user?.type === "Reseller";

export const filterResellerProviderDomains = (providerDomains, query) => {
  const normalized = String(query || "")
    .trim()
    .toLowerCase();

  if (!normalized) return providerDomains;

  return providerDomains.filter((item) =>
    item.uriHost.toLowerCase().includes(normalized)
  );
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
};

const ResellerSettings = () => {
  const [providerDomains, setProviderDomains] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogMode, setDialogMode] = useState(null);
  const [editingDomain, setEditingDomain] = useState(null);
  const [uriHost, setUriHost] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const visibleDomains = useMemo(
    () => filterResellerProviderDomains(providerDomains, search),
    [providerDomains, search]
  );

  useEffect(() => {
    const loadProviderDomains = async () => {
      setLoading(true);
      try {
        const response = await apiService.getResellerProviderDomains();

        setProviderDomains(response?.data?.providerDomains || []);
      } catch (error) {
        toast.error(error.message || "Failed to load provider domains");
      } finally {
        setLoading(false);
      }
    };

    loadProviderDomains();
  }, []);

  const closeEditor = () => {
    if (saving) return;
    setDialogMode(null);
    setEditingDomain(null);
    setUriHost("");
  };

  const openCreate = () => {
    setEditingDomain(null);
    setUriHost("");
    setDialogMode("create");
  };

  const openEdit = (providerDomain) => {
    setEditingDomain(providerDomain);
    setUriHost(providerDomain.uriHost);
    setDialogMode("edit");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!uriHost.trim() || saving) return;

    setSaving(true);
    try {
      const response =
        dialogMode === "edit"
          ? await apiService.updateResellerProviderDomain(editingDomain.id, {
              uriHost,
            })
          : await apiService.createResellerProviderDomain({ uriHost });
      const saved = response?.data?.providerDomain;

      if (saved) {
        setProviderDomains((current) => {
          const next =
            dialogMode === "edit"
              ? current.map((item) => (item.id === saved.id ? saved : item))
              : [...current, saved];

          return next.sort((left, right) =>
            left.uriHost.localeCompare(right.uriHost)
          );
        });
      }
      toast.success(
        dialogMode === "edit"
          ? "Provider domain updated"
          : "Provider domain added"
      );
      setDialogMode(null);
      setEditingDomain(null);
      setUriHost("");
    } catch (error) {
      toast.error(
        error.message ||
          (dialogMode === "edit"
            ? "Failed to update provider domain"
            : "Failed to add provider domain")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;

    setDeleting(true);
    try {
      await apiService.deleteResellerProviderDomain(deleteTarget.id);
      setProviderDomains((current) =>
        current.filter((item) => item.id !== deleteTarget.id)
      );
      setDeleteTarget(null);
      toast.success("Provider domain deleted");
    } catch (error) {
      toast.error(error.message || "Failed to delete provider domain");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="relative overflow-hidden rounded-2xl mb-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(600px 220px at 85% -20%, rgba(139, 92, 246, 0.22), transparent 60%), radial-gradient(500px 200px at 10% 120%, rgba(34, 211, 238, 0.12), transparent 60%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.6), transparent)",
          }}
        />
        <div className="relative p-5 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
          <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/30 ring-1 ring-white/10">
              <i className="fas fa-server text-white text-base sm:text-lg" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mb-1">
                <span className="font-mono text-[10px] font-semibold tracking-[0.16em] uppercase text-primary-600 dark:text-primary-300">
                  Provider Directory
                </span>
                {providerDomains.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-wider uppercase text-gray-400 dark:text-gray-500">
                    <span className="w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-500" />
                    {providerDomains.length} total
                  </span>
                )}
              </div>
              <h1 className="text-xl sm:text-[26px] font-semibold text-gray-900 dark:text-white tracking-tight leading-tight">
                Provider Domains
              </h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 max-w-xl leading-relaxed">
                Manage the saved provider hosts shown in Add Playlist. New hosts
                entered there are saved automatically after a successful
                addition. Existing playlists keep their current host when an
                option is edited or removed.
              </p>
            </div>
          </div>
          <div className="sm:flex-shrink-0 sm:ml-auto">
            <button
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 hover:shadow-xl hover:shadow-primary-500/40 hover:-translate-y-0.5 text-white px-5 py-3 text-sm font-semibold transition-all ring-1 ring-white/10"
              type="button"
              onClick={openCreate}
            >
              <i className="fas fa-plus text-xs" />
              Add Domain
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-4">
        <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-5 gap-y-2.5">
          <div className="flex items-center gap-2 sm:gap-2.5">
            <i className="fas fa-globe text-xs text-gray-400 dark:text-gray-500" />
            <span className="hidden sm:inline text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.1em]">
              Provider Hosts
            </span>
          </div>
          <div className="relative w-full sm:flex-1 sm:w-auto sm:min-w-[220px] sm:ml-auto">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              aria-label="Search provider domains"
              className="w-full text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-white placeholder-gray-400 pl-9 pr-9 py-2 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white dark:focus:bg-gray-900/80 focus:outline-none transition-all"
              placeholder="Search by provider host..."
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <button
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                type="button"
                onClick={() => setSearch("")}
              >
                <i className="fas fa-times text-[10px]" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Provider Host
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Updated
                </th>
                <th className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {loading && (
                <tr>
                  <td
                    className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                    colSpan="3"
                  >
                    <i className="fas fa-circle-notch fa-spin mr-2" />
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && visibleDomains.length === 0 && (
                <tr>
                  <td className="px-4 py-14 text-center" colSpan="3">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center ring-1 ring-primary-500/20">
                      <i className="fas fa-server text-primary-500 text-base" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {providerDomains.length === 0
                        ? "No provider domains configured"
                        : "No provider domains match the current search"}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                      {providerDomains.length === 0
                        ? "Add a saved option here, or enter a new host while adding a playlist."
                        : "Try a different provider host or clear the search."}
                    </p>
                  </td>
                </tr>
              )}
              {!loading &&
                visibleDomains.map((providerDomain) => (
                  <tr
                    className="hover:bg-gray-50 dark:hover:bg-gray-900/30"
                    key={providerDomain.id}
                  >
                    <td className="px-3 sm:px-4 py-3 align-top">
                      <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center flex-shrink-0 ring-1 ring-primary-500/20">
                          <i className="fas fa-globe text-primary-500 text-xs" />
                        </span>
                        <div className="min-w-0">
                          <code className="block text-sm font-medium text-gray-900 dark:text-white break-all">
                            {providerDomain.uriHost}
                          </code>
                          <div className="sm:hidden mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                            Updated {formatDateTime(providerDomain.updatedAt)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 align-top">
                      {formatDateTime(providerDomain.updatedAt)}
                    </td>
                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap text-right align-top">
                      <button
                        className="text-gray-400 hover:text-primary-500 transition-colors p-1.5"
                        title="Edit provider domain"
                        type="button"
                        onClick={() => openEdit(providerDomain)}
                      >
                        <i className="fas fa-pen" />
                      </button>
                      <button
                        className="text-gray-400 hover:text-red-500 transition-colors p-1.5"
                        title="Delete provider domain"
                        type="button"
                        onClick={() => setDeleteTarget(providerDomain)}
                      >
                        <i className="fas fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <Transition appear as={Fragment} show={dialogMode !== null}>
        <Dialog as="div" className="relative z-50" onClose={closeEditor}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-gray-200 dark:ring-white/5 transition-all">
                  <form onSubmit={handleSave}>
                    <div className="px-7 pt-7 pb-5">
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/30 ring-1 ring-white/10">
                          <i className="fas fa-server text-white text-base" />
                        </div>
                        <div>
                          <Dialog.Title className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                            {dialogMode === "edit"
                              ? "Edit Provider Domain"
                              : "Add Provider Domain"}
                          </Dialog.Title>
                          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                            This host will be available in Add Playlist.
                          </p>
                        </div>
                      </div>
                      <label
                        className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.1em] mb-2"
                        htmlFor="reseller-provider-domain"
                      >
                        Provider Host
                      </label>
                      <div className="relative">
                        <i className="fas fa-globe absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          autoFocus
                          autoComplete="off"
                          className="w-full text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-white placeholder-gray-400 pl-10 pr-4 py-3 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:bg-white dark:focus:bg-gray-900/80 focus:outline-none transition-all"
                          disabled={saving}
                          id="reseller-provider-domain"
                          placeholder="https://provider.example"
                          type="text"
                          value={uriHost}
                          onChange={(event) => setUriHost(event.target.value)}
                        />
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        HTTP and HTTPS are supported. Bare domains default to
                        HTTPS.
                      </p>
                    </div>
                    <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30 rounded-b-2xl flex justify-end gap-2">
                      <button
                        className="px-5 py-2.5 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                        disabled={saving}
                        type="button"
                        onClick={closeEditor}
                      >
                        Cancel
                      </button>
                      <button
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:shadow-lg hover:shadow-primary-500/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:shadow-none disabled:translate-y-0 transition-all"
                        disabled={!uriHost.trim() || saving}
                        type="submit"
                      >
                        {saving ? (
                          <>
                            <i className="fas fa-circle-notch fa-spin text-xs" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check text-xs" />
                            {dialogMode === "edit"
                              ? "Save Changes"
                              : "Add Domain"}
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear as={Fragment} show={!!deleteTarget}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => !deleting && setDeleteTarget(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-gray-200 dark:ring-white/5 transition-all">
                  <div className="px-7 pt-7 pb-5">
                    <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-4 ring-1 ring-red-500/20">
                      <i className="fas fa-trash text-red-500 text-base" />
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                      Delete this provider domain?
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      <code className="font-semibold">
                        {deleteTarget?.uriHost}
                      </code>{" "}
                      will no longer appear as a saved option. Existing
                      playlists will keep their current host, and using this
                      host again will save it automatically.
                    </p>
                  </div>
                  <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30 rounded-b-2xl flex justify-end gap-2">
                    <button
                      className="px-5 py-2.5 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                      disabled={deleting}
                      type="button"
                      onClick={() => setDeleteTarget(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white hover:shadow-lg hover:shadow-red-500/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:shadow-none disabled:translate-y-0 transition-all"
                      disabled={deleting}
                      type="button"
                      onClick={handleDelete}
                    >
                      {deleting ? (
                        <>
                          <i className="fas fa-circle-notch fa-spin text-xs" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-trash text-xs" />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default ResellerSettings;
